/** Items unique to the tour screen. Spec §2.1, §2.2, §2.3, §2.4, §2.5, §2.6. */
import { useState } from 'react';

import type { TourItem } from '../mappers/tourDetails';
import { imageUrl } from '../images';
import { AndroidText } from './Items';
import { cardMargins, marginStyle } from './margins';

export interface TourItemContext {
  imagesBase: string;
  imageWidthPx: number;
}

const rail: React.CSSProperties = {
  position: 'absolute',
  left: 24,
  width: 1,
  background: 'var(--and-color-divider)',
};

/** item_my_trip_header.xml — title and subtitle centred, description LEFT-aligned.
 *  Each view is hidden independently, so a day with only a description collapses. */
function MyTripHeader({
  title,
  subTitle,
  descriptionHtml,
}: {
  title: string | null;
  subTitle: string | null;
  descriptionHtml: string | null;
}) {
  return (
    <div>
      {title && (
        <div
          className="and-type-legacy-headline2"
          style={{ marginLeft: 32, marginRight: 32, marginBottom: 6, textAlign: 'center' }}
        >
          {title}
        </div>
      )}
      {subTitle && (
        <div
          className="and-type-body1-dark-medium"
          style={{ marginLeft: 32, marginRight: 32, marginBottom: 16, textAlign: 'center' }}
        >
          {subTitle}
        </div>
      )}
      {descriptionHtml && (
        <AndroidText
          html={descriptionHtml}
          className="and-type-body1-dark-medium"
          style={{ marginLeft: 32, marginRight: 32, marginBottom: 16 }}
        />
      )}
    </div>
  );
}

/** item_timeline_day.xml — REGULAR puts the circle at 10dp, EXTENDED at 24dp.
 *  SMALL shrinks the circle to 10dp (a slot with no mappable activity). */
function TimelineDay({
  text,
  symbolNumber,
  position,
  style,
  size,
}: Omit<Extract<TourItem, { kind: 'timelineDay' }>, 'kind' | 'key'>) {
  const circleTop = style === 'EXTENDED' ? 24 : 10;
  const circleSize = size === 'SMALL' ? 10 : 24;
  const topLine = position === 'MIDDLE' || position === 'BOTTOM';
  const bottomLine = position === 'TOP' || position === 'MIDDLE';

  return (
    <div style={{ position: 'relative', minHeight: circleTop + circleSize + 16 }}>
      {topLine && <div style={{ ...rail, top: 0, height: circleTop }} />}
      <div
        style={{
          position: 'absolute',
          left: 24 - circleSize / 2,
          top: circleTop,
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          border: '1px solid var(--and-color-divider)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {symbolNumber !== null && (
          <span className="and-type-number" style={{ lineHeight: 1 }}>
            {symbolNumber}
          </span>
        )}
      </div>
      {bottomLine && <div style={{ ...rail, top: circleTop + circleSize, bottom: 0 }} />}
      {/* @style/Overline is allCaps, but the layout sets textAllCaps=false. */}
      <div
        className="and-type-overline"
        style={{
          marginLeft: 50,
          marginRight: 16,
          paddingTop: circleTop,
          paddingBottom: 16,
          textTransform: 'none',
          minHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </div>
    </div>
  );
}

/** item_text_based_activity.xml — the rail runs the FULL item height.
 *  Collapsed at 4 lines; the toggle only appears when the text actually overflows. */
function TextBasedActivity({
  html,
  buttonText,
  expanded,
  lineVisible,
  onToggle,
}: {
  html: string;
  buttonText: string;
  expanded: boolean;
  lineVisible: boolean;
  onToggle: () => void;
}) {
  const [overflows, setOverflows] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      {lineVisible && <div style={{ ...rail, top: 0, bottom: 0 }} />}
      <div style={{ marginLeft: 50, marginRight: 16 }}>
        <div
          ref={(el) => {
            if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
          }}
          style={
            expanded
              ? { marginBottom: 16 }
              : {
                  marginBottom: 16,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }
          }
        >
          <AndroidText html={html} className="and-type-body2-dark" />
        </div>
        {(overflows || expanded) && (
          <button
            type="button"
            onClick={onToggle}
            className="and-type-legacy-subtitle1"
            style={{
              border: 0,
              background: 'transparent',
              padding: 0,
              marginBottom: 32,
              color: 'var(--and-color-blue)',
              cursor: 'pointer',
            }}
          >
            {buttonText}
          </button>
        )}
      </div>
    </div>
  );
}

/** item_my_trip_alternatives.xml — a snapping horizontal list, 34dp padding each side.
 *  item_activity_card_activity.xml — 110dp tall, 102dp image, radius 16dp, elevation 4dp. */
function Alternatives({
  cards,
  showLine,
  ctx,
}: {
  cards: Extract<TourItem, { kind: 'alternatives' }>['cards'];
  showLine: boolean;
  ctx: TourItemContext;
}) {
  const m = cardMargins('NESTED');
  return (
    <div style={{ position: 'relative' }}>
      {showLine && <div style={{ ...rail, top: 0, bottom: 0 }} />}
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingLeft: 34,
          paddingRight: 34,
        }}
      >
        {cards.map((card) => {
          const src = card.image ? imageUrl(ctx.imagesBase, card.image, ctx.imageWidthPx) : null;
          return (
            <div
              key={card.key}
              style={{
                ...marginStyle(m),
                flex: '0 0 auto',
                width: 'calc(100% - 8px)',
                height: 110,
                scrollSnapAlign: 'start',
                display: 'flex',
                borderRadius: 16,
                overflow: 'hidden',
                background: 'var(--and-color-white)',
                boxShadow: '0 2px 4px rgb(0 0 0 / 0.16)',
              }}
            >
              <div style={{ flex: '0 0 102px', height: 110, background: 'var(--and-color-surface)' }}>
                {src && <img src={src} alt="" style={{ width: 102, height: 110, objectFit: 'cover' }} />}
              </div>
              <div
                style={{
                  alignSelf: 'center',
                  marginLeft: 14,
                  marginRight: 16,
                  minWidth: 0,
                }}
              >
                <div
                  className="and-type-legacy-subtitle1"
                  style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {card.title}
                </div>
                {card.descriptionText && (
                  <div
                    className="and-type-body2-dark-medium"
                    style={{
                      marginTop: 2,
                      marginBottom: 14,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {card.descriptionText}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** item_my_trip_transport.xml — a 10dp circle on the rail, icon at 43.5dp, text at 81.5dp.
 *  Both rail segments are ALWAYS visible here, unlike the timeline item. */
function Transport({ text, iconName }: { text: string; iconName: string | null }) {
  return (
    <div style={{ position: 'relative', minHeight: 58, display: 'flex', alignItems: 'center' }}>
      <div style={{ ...rail, top: 0, height: 32 }} />
      <div
        style={{
          position: 'absolute',
          left: 19.5,
          top: 32,
          width: 10,
          height: 10,
          borderRadius: '50%',
          border: '1px solid var(--and-color-divider)',
        }}
      />
      <div style={{ ...rail, top: 42, bottom: 0 }} />
      {iconName && (
        <div
          style={{
            position: 'absolute',
            left: 43.5,
            top: 20,
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--and-color-color-dark-medium)',
            fontSize: 14,
          }}
          title={iconName}
        >
          →
        </div>
      )}
      <div
        className="and-type-body1"
        style={{
          marginLeft: 81.5,
          marginRight: 16,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </div>
    </div>
  );
}

export function TourItemView({
  item,
  ctx,
  onToggleSlot,
}: {
  item: TourItem;
  ctx: TourItemContext;
  onToggleSlot: (slotId: number) => void;
}) {
  switch (item.kind) {
    case 'myTripHeader':
      return (
        <MyTripHeader title={item.title} subTitle={item.subTitle} descriptionHtml={item.descriptionHtml} />
      );
    case 'timelineDay': {
      // Strip `key` before spreading: the caller already keys this element, and React 18
      // warns that a key arriving via spread is not a key at all.
      const { key, kind, ...rail } = item;
      void key;
      void kind;
      return <TimelineDay {...rail} />;
    }
    case 'textBasedActivity':
      return (
        <TextBasedActivity
          html={item.html}
          buttonText={item.buttonText}
          expanded={item.expanded}
          lineVisible={item.lineVisible}
          onToggle={() => onToggleSlot(item.slotId)}
        />
      );
    case 'alternatives':
      return <Alternatives cards={item.cards} showLine={item.showLine} ctx={ctx} />;
    case 'transport':
      return <Transport text={item.text} iconName={item.iconName} />;
    default:
      return null;
  }
}
